import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import {
    LicensePricingType,
    LicenseVariablePriceByType,
    PlatformLicenseDTO,
    PlatformLicenseEmployeeCountingType
} from '@iota/iota-management-portal-v4-client';
import { PlatformLicenseEntity } from 'src/app/store/entity-models';
import {
    PlatformLicenseEmployeeCountingTypeLabels,
    PlatformLicensePricingTypeLabels,
    PlatformLicenseVariablePriceByLabels
} from '../../constants-and-enums/platform-license-types';
import { Subscription } from 'rxjs';

// Type-safe form with values from PlatformLicenseDTO
type PlatformLicenseControls = Partial<Record<keyof PlatformLicenseDTO, FormControl>>;

@Component({
    standalone: false,
    selector: 'app-platform-license-form',
    templateUrl: './platform-license-form.component.html',
    styleUrls: ['./platform-license-form.component.scss']
})
export class PlatformLicenseFormComponent implements OnInit, OnChanges, OnDestroy {
    @Input() platformLicense: PlatformLicenseEntity;
    @Input() editMode = false;

    @Output() formValid = new EventEmitter<boolean>();

    formSubscription: Subscription;
    formValidSubscription: Subscription;

    platformLicenseForm = this.fb.group<PlatformLicenseControls>({
        licenseNameAttribute: new FormControl('', { validators: [Validators.required, Validators.maxLength(255)] }),
        description: new FormControl('', { validators: [Validators.required] }),
        licensePricingType: new FormControl<LicensePricingType>(null, { validators: [Validators.required] }),
        licenseVariablePriceBy: new FormControl<LicenseVariablePriceByType>(null, { validators: [Validators.required] }),
        employeeCountingType: new FormControl<PlatformLicenseEmployeeCountingType>(null, { validators: [Validators.required] })
    });

    licensePricingTypeOptions = Object.entries(PlatformLicensePricingTypeLabels).map(([value, label]) => ({
        value,
        label
    }));
    licenseVariablePriceByOptions = Object.entries(PlatformLicenseVariablePriceByLabels).map(([value, label]) => ({
        value,
        label
    }));
    employeeCountingTypeOptions = Object.entries(PlatformLicenseEmployeeCountingTypeLabels).map(([value, label]) => ({
        value,
        label
    }));

    // Fields that are hidden depending on the pricing type configuration
    showVariablePriceBy = false;
    showEmployeeCountingType = false;

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        this.formValidSubscription = this.platformLicenseForm.valueChanges.subscribe(() => {
            this.formValid.emit(this.platformLicenseForm.valid);
        });
        this.formSubscription = this.platformLicenseForm.controls.licensePricingType.valueChanges.subscribe(() => {
            this.enableOrDisableFieldsDependingOnPricingType();
        });

        this.initForm(this.editMode);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const previousPlatformLicense = changes?.platformLicense?.previousValue;
        const currentPlatformLicense = changes?.platformLicense?.currentValue;

        const previousEditMode = changes?.editMode?.previousValue;
        const currentEditMode = changes?.editMode?.currentValue;

        if (previousPlatformLicense !== currentPlatformLicense || previousEditMode !== currentEditMode) {
            this.initForm(this.editMode);
        }
    }

    ngOnDestroy(): void {
        this.formValidSubscription?.unsubscribe();
        this.formSubscription?.unsubscribe();
    }

    async initForm(enableEditing = false) {
        if (this.platformLicense) {
            this.platformLicenseForm.patchValue({
                ...this.platformLicense
            });
        }

        if (enableEditing) {
            this.editMode = true;
            this.platformLicenseForm.enable();
            this.enableOrDisableFieldsDependingOnPricingType();
        } else {
            this.editMode = false;
            this.platformLicenseForm.disable();
        }
    }

    enableEditing() {
        this.initForm(true);
    }

    disableEditingAndDontInitializeForm() {
        this.editMode = false;
        this.platformLicenseForm.disable();
    }

    enableOrDisableFieldsDependingOnPricingType() {
        const pricingType = this.platformLicenseForm.controls.licensePricingType.value as LicensePricingType;
        if (!pricingType) return;

        this.showVariablePriceBy = this.shouldVariablePriceByBeVisible(pricingType);
        if (this.showVariablePriceBy) {
            this.platformLicenseForm.controls.licenseVariablePriceBy.enable();
        } else {
            this.platformLicenseForm.controls.licenseVariablePriceBy.disable();
        }

        this.showEmployeeCountingType = this.shouldEmployeeCountingTypeBeVisible(pricingType);
        if (this.showEmployeeCountingType) {
            this.platformLicenseForm.controls.employeeCountingType.enable();
        } else {
            this.platformLicenseForm.controls.employeeCountingType.disable();
        }
    }

    shouldVariablePriceByBeVisible(pricingType: LicensePricingType) {
        return pricingType === LicensePricingType.SINGLE_USER_LICENSE || pricingType === LicensePricingType.SCALE_PRICE_LICENSE;
    }

    shouldEmployeeCountingTypeBeVisible(pricingType: LicensePricingType) {
        return pricingType === LicensePricingType.SCALE_PRICE_LICENSE;
    }

    getUpsertedPlatformLicense(): PlatformLicenseDTO {
        if (!this.platformLicenseForm.valid) return null;

        const existingPlatformLicense = !!this.platformLicense ? this.platformLicense : {};
        return { ...existingPlatformLicense, ...this.platformLicenseForm.getRawValue() };
    }
}
